import React, { useState, useEffect } from 'react';
import { useRecruitment } from '../../context/RecruitmentContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  RefreshCw, User, Mail, Phone, Building2, Clock, Calendar,
  ChevronRight, GripVertical, Plus, Briefcase
} from 'lucide-react';
import { toast } from 'sonner';

const KanbanBoard = ({ onViewCandidate, getCandidateUrl }) => {
  const {
    candidates, jobs, pipelineTemplates, loading,
    fetchCandidates, fetchAllCandidates, fetchJobs, fetchPipelineTemplates,
    updateCandidateStage, patchCandidateLocal, createInterview
  } = useRecruitment();

  const [selectedJob, setSelectedJob] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [stages, setStages] = useState([]);
  const [draggedCandidate, setDraggedCandidate] = useState(null);
  
  // Interview scheduling dialog
  const [showInterviewDialog, setShowInterviewDialog] = useState(false);
  const [interviewCandidate, setInterviewCandidate] = useState(null);
  const [interviewForm, setInterviewForm] = useState({
    round_name: '',
    round_number: 1,
    interviewer_name: '',
    scheduled_at: ''
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    // Set stages based on selected template or default
    if (selectedJob && selectedJob !== 'all') {
      const job = jobs.find(j => j.id === selectedJob);
      if (job?.pipeline_template_id) {
        const template = pipelineTemplates.find(t => t.id === job.pipeline_template_id);
        if (template) {
          setStages(template.stages);
          setSelectedTemplate(template);
          return;
        }
      }
    }
    // Use default template
    const defaultTemplate = pipelineTemplates.find(t => t.is_default);
    if (defaultTemplate) {
      setStages(defaultTemplate.stages);
      setSelectedTemplate(defaultTemplate);
    } else if (pipelineTemplates.length > 0) {
      setStages(pipelineTemplates[0].stages);
      setSelectedTemplate(pipelineTemplates[0]);
    }
  }, [selectedJob, pipelineTemplates, jobs]);

  useEffect(() => {
    if (selectedJob && selectedJob !== 'all') {
      fetchAllCandidates({ job_id: selectedJob });
    } else {
      fetchAllCandidates();
    }
  }, [selectedJob]);

  // Candidates are NOT fetched here. The [selectedJob] effect above already
  // runs on mount (initial value 'all') and on every job change, so fetching
  // here too downloaded the whole collection twice on every Pipeline open.
  // It was also a race: pick a job quickly and this unfiltered fetch could land
  // AFTER the filtered one and overwrite it, showing the wrong job's board.
  const loadInitialData = async () => {
    await Promise.all([
      fetchJobs(),
      fetchPipelineTemplates()
    ]);
  };

  // Reload the board's candidates with the board's OWN job filter.
  const reloadBoardCandidates = () => (
    selectedJob && selectedJob !== 'all'
      ? fetchAllCandidates({ job_id: selectedJob })
      : fetchAllCandidates()
  );

  // Refresh function that respects the job filter
  const handleRefresh = async () => {
    await Promise.all([
      fetchJobs(),
      fetchPipelineTemplates()
    ]);
    reloadBoardCandidates();
  };

  const getCandidatesByStage = (stage) => {
    return candidates.filter(c => c.current_stage === stage);
  };

  const handleDragStart = (e, candidate) => {
    setDraggedCandidate(candidate);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    const cand = draggedCandidate;
    setDraggedCandidate(null);
    if (!cand || cand.current_stage === targetStage) return;

    // Move the card now, instead of waiting for the server and then reloading.
    // The old path reloaded the shared list UNFILTERED and first-page-only, so
    // a board filtered to one job collapsed to the newest candidates across
    // ALL jobs after every drag. The stage endpoint changes only current_stage
    // and updated_at (not status), so this local patch mirrors it faithfully.
    patchCandidateLocal(cand.id, { current_stage: targetStage });

    const result = await updateCandidateStage(
      cand.id,
      targetStage,
      `Moved from ${cand.current_stage} to ${targetStage}`,
      { refresh: false },
    );

    if (result.success) {
      toast.success(`Moved ${cand.name} to ${targetStage}`);
    } else {
      toast.error(result.error);
      // Deliberately NOT a local rollback to the old stage. The endpoint
      // writes the stage FIRST and then runs history, audit and email steps
      // that can each fail afterwards — so an error does not mean the stage
      // is unchanged. Undoing the move locally could show the old column while
      // the server holds the new one. Re-read the truth, with this board's
      // filter, instead.
      reloadBoardCandidates();
    }
  };

  const handleScheduleInterview = (candidate) => {
    setInterviewCandidate(candidate);
    setInterviewForm({
      round_name: '',
      round_number: 1,
      interviewer_name: '',
      scheduled_at: ''
    });
    setShowInterviewDialog(true);
  };

  const handleSubmitInterview = async () => {
    if (!interviewForm.round_name || !interviewForm.scheduled_at) {
      toast.error('Round name and scheduled time are required');
      return;
    }

    const result = await createInterview({
      candidate_id: interviewCandidate.id,
      job_id: interviewCandidate.job_id,
      ...interviewForm
    });

    if (result.success) {
      toast.success('Interview scheduled');
      setShowInterviewDialog(false);
    } else {
      toast.error(result.error);
    }
  };

  const getStageColor = (idx) => {
    const colors = [
      'border-t-blue-500',
      'border-t-cyan-500',
      'border-t-purple-500',
      'border-t-amber-500',
      'border-t-emerald-500',
      'border-t-rose-500'
    ];
    return colors[idx % colors.length];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Pipeline Board</h2>
          <p className="text-slate-500">Drag and drop candidates through stages</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedJob} onValueChange={setSelectedJob}>
            <SelectTrigger className="w-[200px]" data-testid="kanban-job-filter">
              <SelectValue placeholder="All Jobs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Jobs</SelectItem>
              {jobs.map(job => (
                <SelectItem key={job.id} value={job.id}>{job.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleRefresh} data-testid="refresh-kanban">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Pipeline info */}
      {selectedTemplate && (
        <div className="text-sm text-slate-500">
          Using pipeline: <span className="font-medium text-slate-700">{selectedTemplate.name}</span>
        </div>
      )}

      {/* Kanban Board */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {stages.map((stage, idx) => {
            const stageCandidates = getCandidatesByStage(stage);
            return (
              <div
                key={stage}
                className="flex-shrink-0 w-[300px]"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage)}
              >
                <Card className={`border-t-4 ${getStageColor(idx)}`}>
                  <CardHeader className="py-3 px-4">
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-sm font-medium">{stage}</CardTitle>
                      <Badge variant="secondary" className="font-normal">
                        {stageCandidates.length}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="p-2">
                    <ScrollArea className="h-[calc(100vh-320px)]">
                      <div className="space-y-2 p-1">
                        {stageCandidates.length === 0 ? (
                          <div className="text-center py-8 text-slate-400 text-sm">
                            No candidates
                          </div>
                        ) : (
                          stageCandidates.map((candidate) => (
                            <div
                              key={candidate.id}
                              draggable
                              onDragStart={(e) => handleDragStart(e, candidate)}
                              className={`p-3 bg-white border rounded-lg cursor-grab hover:shadow-md transition-shadow ${
                                draggedCandidate?.id === candidate.id ? 'opacity-50' : ''
                              }`}
                              data-testid={`kanban-card-${candidate.id}`}
                            >
                              <div className="flex items-start gap-2">
                                <GripVertical className="w-4 h-4 text-slate-300 mt-1 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <a
                                    href={getCandidateUrl ? getCandidateUrl(candidate.id) : '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-medium text-sm truncate cursor-pointer hover:text-purple-600 hover:underline block"
                                    data-testid={`kanban-card-name-${candidate.id}`}
                                  >
                                    {candidate.name}
                                  </a>
                                  {candidate.current_company && (
                                    <p className="text-xs text-slate-500 flex items-center gap-1 truncate">
                                      <Building2 className="w-3 h-3" /> {candidate.current_company}
                                    </p>
                                  )}
                                  {candidate.years_of_experience !== null && (
                                    <p className="text-xs text-slate-500 flex items-center gap-1">
                                      <Clock className="w-3 h-3" /> {candidate.years_of_experience} yrs
                                    </p>
                                  )}
                                  {candidate.skills?.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                      {candidate.skills.slice(0, 3).map(skill => (
                                        <Badge key={skill} variant="secondary" className="text-xs px-1 py-0">
                                          {skill}
                                        </Badge>
                                      ))}
                                      {candidate.skills.length > 3 && (
                                        <Badge variant="secondary" className="text-xs px-1 py-0">
                                          +{candidate.skills.length - 3}
                                        </Badge>
                                      )}
                                    </div>
                                  )}
                                  <div className="mt-2 flex gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 px-2 text-xs"
                                      onClick={() => handleScheduleInterview(candidate)}
                                    >
                                      <Calendar className="w-3 h-3 mr-1" /> Interview
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      )}

      {/* Interview Scheduling Dialog */}
      <Dialog open={showInterviewDialog} onOpenChange={setShowInterviewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule Interview</DialogTitle>
            <DialogDescription>
              Schedule an interview for {interviewCandidate?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Interview Round *</Label>
              <Select 
                value={interviewForm.round_name} 
                onValueChange={(v) => setInterviewForm({ ...interviewForm, round_name: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select round type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Phone Screen">Phone Screen</SelectItem>
                  <SelectItem value="Technical Round">Technical Round</SelectItem>
                  <SelectItem value="Coding Round">Coding Round</SelectItem>
                  <SelectItem value="System Design">System Design</SelectItem>
                  <SelectItem value="HR Round">HR Round</SelectItem>
                  <SelectItem value="Manager Round">Manager Round</SelectItem>
                  <SelectItem value="Leadership Round">Leadership Round</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Round Number</Label>
              <Input
                type="number"
                min="1"
                value={interviewForm.round_number}
                onChange={(e) => setInterviewForm({ ...interviewForm, round_number: parseInt(e.target.value) || 1 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Interviewer Name</Label>
              <Input
                value={interviewForm.interviewer_name}
                onChange={(e) => setInterviewForm({ ...interviewForm, interviewer_name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-2">
              <Label>Scheduled Date & Time *</Label>
              <Input
                type="datetime-local"
                value={interviewForm.scheduled_at}
                onChange={(e) => setInterviewForm({ ...interviewForm, scheduled_at: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInterviewDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmitInterview}>Schedule Interview</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KanbanBoard;
